import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { queryKeys, useApiMutation, useApiQuery } from '../api/hooks';
import { Icon } from './Icon';
import type { StaffNotificationCountResponse, StaffNotificationResponse } from '../api/types';

/**
 * What the clinic has told this member of staff (§22, FR-1001).
 *
 * The six staff-directed rows of the notification matrix land here: a worklist opening, stock
 * running out, a machine due for service, a warranty ending, an invoice, and a chat message. Before
 * M9 every one of them was a panel somebody had to remember to open, which is the deferral DEC-090
 * recorded and this closes.
 *
 * Polled rather than pushed. The alternative is a second WebSocket subscription per session for
 * something whose events arrive a handful of times a day, and a badge that is a minute stale has
 * cost nobody anything.
 */
export function NotificationBell() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const count = useApiQuery<StaffNotificationCountResponse>(
    queryKeys.staffNotificationCount(),
    '/me/notifications/unread',
  );

  const list = useApiQuery<StaffNotificationResponse[]>(
    queryKeys.staffNotifications(),
    '/me/notifications?limit=20',
    { enabled: open },
  );

  const markAllRead = useApiMutation<void, void>(
    () => ({ path: '/me/notifications/read', options: { method: 'POST' } }),
    { invalidates: [queryKeys.staffNotificationCount(), queryKeys.staffNotifications()] },
  );

  const unread = count.data?.unread ?? 0;

  return (
    <div className="bell">
      <button
        type="button"
        className="bell__button"
        aria-expanded={open}
        aria-label={t('notificationsBell.label', { count: unread })}
        onClick={() => setOpen((current) => !current)}
      >
        {/* A drawn bell rather than 🔔, which renders as a different picture on every operating
            system and arrives full-colour on a page with one accent. */}
        <Icon name="bell" size={20} />
        {unread > 0 ? (
          <span className="bell__count" aria-hidden="true">
            {unread > 9 ? '9+' : unread}
          </span>
        ) : null}
      </button>

      {open ? (
        <div className="bell__panel panel" role="dialog" aria-label={t('notificationsBell.title')}>
          <header className="panel__heading">
            <h2>{t('notificationsBell.title')}</h2>
            {unread > 0 ? (
              <button type="button" className="button button--ghost button--compact" onClick={() => markAllRead.mutate()}>
                {t('notificationsBell.markAllRead')}
              </button>
            ) : null}
          </header>

          {(list.data ?? []).length === 0 ? (
            <p className="muted">{t('notificationsBell.empty')}</p>
          ) : (
            <ul className="bell__items">
              {(list.data ?? []).map((item) => (
                <li key={item.id} className={item.readAt ? 'bell__item' : 'bell__item is-unread'}>
                  <span className="bell__item-title">{t(`notificationsBell.events.${item.event}`, item.event ?? '')}</span>
                  <span className="muted">{item.body}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
